/* Nodal word glyph model (Subroutine 1 of "Writing in Kohd").
   - Consecutive letters in the same node form one visit; each letter adds dots
     equal to its position in the node to the trace leaving that visit.
   - Returning to a node adds a ring; the visit connects to that ring.
   - The first visit holds the charge, the last visit feeds the ground.
   - When the used nodes leave the glyph's place on the 3x3 grid vague, a null
     modifier marks an unused, remote corner. */

import { letterCell } from './alphabet';

export interface Cell {
  col: number;
  row: number;
}

export interface Visit extends Cell {
  letters: string[];
  dots: number[];
  /** Which ring of the node this visit uses (1 = the node circle itself). */
  ring: number;
}

export interface GlyphNode extends Cell {
  rings: number;
}

export interface WordGlyph {
  word: string;
  visits: Visit[];
  nodes: GlyphNode[];
  nullCorner: Cell | null;
}

const CORNERS: Cell[] = [
  { col: 0, row: 0 },
  { col: 2, row: 0 },
  { col: 0, row: 2 },
  { col: 2, row: 2 },
];

const sameCell = (a: Cell, b: Cell) => a.col === b.col && a.row === b.row;

export function buildWordGlyph(word: string): WordGlyph {
  const visits: Visit[] = [];
  const nodes: GlyphNode[] = [];

  for (const ch of word) {
    const cell = letterCell(ch);
    if (!cell) continue;
    const letter = ch.toUpperCase();
    const last = visits[visits.length - 1];
    if (last && sameCell(last, cell)) {
      last.letters.push(letter);
      last.dots.push(cell.position);
      continue;
    }
    let node = nodes.find((n) => sameCell(n, cell));
    if (!node) {
      node = { col: cell.col, row: cell.row, rings: 0 };
      nodes.push(node);
    }
    node.rings += 1;
    visits.push({ col: cell.col, row: cell.row, letters: [letter], dots: [cell.position], ring: node.rings });
  }

  return { word, visits, nodes, nullCorner: findNullCorner(nodes) };
}

/* Prefer an unused corner whose row and column are both empty; otherwise the
   last unused corner that shares an empty row or column. */
function findNullCorner(nodes: GlyphNode[]): Cell | null {
  if (nodes.length === 0) return null;
  const rows = new Set(nodes.map((n) => n.row));
  const cols = new Set(nodes.map((n) => n.col));
  if (rows.size === 3 && cols.size === 3) return null;

  let fallback: Cell | null = null;
  for (const corner of CORNERS) {
    if (nodes.some((n) => sameCell(n, corner))) continue;
    const rowFree = !rows.has(corner.row);
    const colFree = !cols.has(corner.col);
    if (rowFree && colFree) return corner;
    if (rowFree || colFree) fallback = corner;
  }
  return fallback;
}
